import { useEffect } from 'react';
import { CapTableInfo } from 'common/types/capTable';
import {
  FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG,
  NET_SHARES_CROSS_REFERENCE_CONFIG,
} from 'pages/CapTable/cell-annotations/configs';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import {
  buildCellKey,
  type CellAnnotationCellKey,
  type CellAnnotationConfig,
  type CellAnnotationState,
  type CellAnnotationType,
  type CellAnnotationWireEntry,
  type CellAnnotationWireMap,
} from './types';
import { wireEntryToState } from './utils/serialize';

// Walk the nested per-parent wire map (field_alias -> annotation_type -> entry)
// and emit one cellKey -> CellAnnotationState pair per annotation. The
// annotation_type is part of the cellKey, so each (parent, field, type) lands
// in its own store slot — no risk of two annotation kinds on one cell
// clobbering each other.
const mergeWireMap = (
  out: Record<CellAnnotationCellKey, CellAnnotationState>,
  parentType: string,
  parentId: number | string | null | undefined,
  wireMap: CellAnnotationWireMap | null | undefined
): void => {
  if (parentId == null || !wireMap) {
    return;
  }
  Object.entries(wireMap).forEach(([fieldAlias, byAnnotationType]) => {
    if (!byAnnotationType) {
      return;
    }
    Object.entries(byAnnotationType).forEach(([annotationType, entry]) => {
      if (!entry) {
        return;
      }
      const cellKey = buildCellKey(parentType, parentId, fieldAlias, annotationType as CellAnnotationType);
      // eslint-disable-next-line no-param-reassign
      out[cellKey] = wireEntryToState(entry as CellAnnotationWireEntry);
    });
  });
};

// Build a blank state for a cell that has a known config but no saved data
// yet. Mirrors the store's internal seedIfMissing shape so editing the popup
// flows into the same store slot as a hydrated state would.
const buildBlankState = (config: CellAnnotationConfig): CellAnnotationState => ({
  rowMode: config.rowMode,
  columnsConfig: config.columns,
  rows: [
    {
      position: 0,
      value: null,
      metadata: {},
      isPlaceholder: config.rowMode === 'multi_row',
    },
  ],
  isDirty: false,
});

const seedIfAbsent = (
  out: Record<CellAnnotationCellKey, CellAnnotationState>,
  cellKey: CellAnnotationCellKey,
  config: CellAnnotationConfig
): void => {
  if (!out[cellKey]) {
    // eslint-disable-next-line no-param-reassign
    out[cellKey] = buildBlankState(config);
  }
};

// Loose shape for child rows we walk for cell-annotation hydration.
// Acquisitions / sales / distributions / CFL items all carry an `id` plus the
// unified `cell_annotations` map; declaring a single type here keeps the
// hydrator decoupled from per-row writable shapes that include many fields we
// don't read.
interface ChildAnnotatedRow {
  id?: number | null;
  cell_annotations?: CellAnnotationWireMap | null;
  sales?: ChildAnnotatedRow[] | null;
  cash_distributions?: ChildAnnotatedRow[] | null;
}

// Seeds the cell-annotation store from a cap-table detail response. Runs
// whenever `captableInfo` changes identity. Covers every parent that ships
// cell_annotations: CapTable, FundOwnershipDetail, shares-ledger children
// (acquisition/sale/distribution), and CashFlowLedgerItem.
//
// For every cell that has a known config we ALSO seed a blank state when no
// saved wire data exists. Without this, the popup's reverseParser calls
// setRows on a missing cellKey, which is a silent no-op — first-time edits
// would never validate or persist.
const useHydrateCaptableCellAnnotations = (captableInfo: CapTableInfo | null | undefined): void => {
  const hydrate = useCellAnnotationStore(store => store.hydrate);

  useEffect(() => {
    if (!captableInfo) {
      return;
    }

    const stateByCellKey: Record<CellAnnotationCellKey, CellAnnotationState> = {};

    mergeWireMap(
      stateByCellKey,
      'captable',
      captableInfo.id,
      captableInfo.cell_annotations as CellAnnotationWireMap | null | undefined
    );
    if (captableInfo.id != null) {
      seedIfAbsent(
        stateByCellKey,
        buildCellKey('captable', captableInfo.id, 'fully_diluted_total', 'cross_reference'),
        FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG
      );
    }

    const fods = captableInfo.fund_ownership?.fund_ownership_detail ?? [];
    fods.forEach(fod => {
      mergeWireMap(
        stateByCellKey,
        'fundownershipdetail',
        fod.id,
        fod.cell_annotations as CellAnnotationWireMap | null | undefined
      );
      if (fod?.id != null) {
        seedIfAbsent(
          stateByCellKey,
          buildCellKey('fundownershipdetail', fod.id, 'net_shares', 'cross_reference'),
          NET_SHARES_CROSS_REFERENCE_CONFIG
        );
      }

      const acquisitions = (fod as { acquisitions?: ChildAnnotatedRow[] | null | undefined }).acquisitions ?? [];
      acquisitions.forEach(acq => {
        mergeWireMap(stateByCellKey, 'sharesacquisition', acq?.id, acq?.cell_annotations);
        (acq?.sales ?? []).forEach(sale =>
          mergeWireMap(stateByCellKey, 'sharessale', sale?.id, sale?.cell_annotations)
        );
        (acq?.cash_distributions ?? []).forEach(dist =>
          mergeWireMap(stateByCellKey, 'cashdistribution', dist?.id, dist?.cell_annotations)
        );
      });
    });

    // CashFlowLedgerItem rows. Virtual rows have no server parent, so they
    // can't carry annotations and are skipped naturally via the id check
    // inside mergeWireMap.
    const cashFlowLedger
      = (captableInfo as { cash_flow_ledger?: ChildAnnotatedRow[] | null | undefined }).cash_flow_ledger ?? [];
    cashFlowLedger.forEach(item => {
      mergeWireMap(stateByCellKey, 'cashflowledgeritem', item?.id, item?.cell_annotations);
    });

    hydrate(stateByCellKey);
  }, [captableInfo, hydrate]);
};

export default useHydrateCaptableCellAnnotations;
